import { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, RefreshCw, FlaskConical, X } from 'lucide-react';
import { blocklist, BlocklistEntry, BlocklistViolation } from '../api/client';

const SCOPE_OPTIONS = [
  { value: '', label: 'All Scopes' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'both', label: 'Both' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'csam', label: 'CSAM' },
  { value: 'real_person', label: 'Real Person' },
  { value: 'self_harm', label: 'Self-Harm' },
  { value: 'sexual', label: 'Sexual' },
  { value: 'terrorism', label: 'Terrorism' },
  { value: 'other', label: 'Other' },
];

const TYPE_OPTIONS = [
  { value: 'word', label: 'Word (whole word boundary)' },
  { value: 'phrase', label: 'Phrase (substring match)' },
  { value: 'pattern', label: 'Pattern (regex)' },
];

const CATEGORY_COLORS: Record<string, string> = {
  csam: 'bg-red-500/20 text-red-400',
  real_person: 'bg-orange-500/20 text-orange-400',
  self_harm: 'bg-yellow-500/20 text-yellow-400',
  sexual: 'bg-pink-500/20 text-pink-400',
  terrorism: 'bg-purple-500/20 text-purple-400',
  other: 'bg-gray-500/20 text-gray-400',
};

const SCOPE_COLORS: Record<string, string> = {
  text: 'bg-blue-500/20 text-blue-400',
  image: 'bg-green-500/20 text-green-400',
  both: 'bg-violet-500/20 text-violet-400',
};

export default function ContentBlocklist() {
  const [entries, setEntries] = useState<BlocklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTestPanel, setShowTestPanel] = useState(false);

  // Filters
  const [filterScope, setFilterScope] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Add form
  const [newValue, setNewValue] = useState('');
  const [newType, setNewType] = useState<'word' | 'phrase' | 'pattern'>('word');
  const [newScope, setNewScope] = useState<'text' | 'image' | 'both'>('both');
  const [newCategory, setNewCategory] = useState<'csam' | 'real_person' | 'self_harm' | 'sexual' | 'terrorism' | 'other'>('other');
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Test
  const [testText, setTestText] = useState('');
  const [testScope, setTestScope] = useState<'text' | 'image'>('text');
  const [testResults, setTestResults] = useState<BlocklistViolation[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);

  async function fetchData() {
    setLoading(true);
    setError(null);
    try {
      const data = await blocklist.list(
        filterScope || undefined,
        filterCategory || undefined
      );
      setEntries(data.entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blocklist');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [filterScope, filterCategory]);

  async function handleAdd() {
    if (!newValue.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      await blocklist.create({
        value: newValue.trim(),
        type: newType,
        scope: newScope,
        category: newCategory,
      });
      setNewValue('');
      setShowAddModal(false);
      fetchData();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this blocklist entry?')) return;
    try {
      await blocklist.delete(id);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  }

  async function handleTest() {
    if (!testText.trim()) return;
    setTesting(true);
    setTestError(null);
    try {
      const data = await blocklist.test(testText, testScope);
      setTestResults(data.violations);
    } catch (err) {
      setTestError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-violet-400" />
          <div>
            <h1 className="text-2xl font-bold text-gradient">Content Filter</h1>
            <p className="text-sm text-gray-400">
              Manage blocklist entries for text and image content filtering
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowTestPanel(!showTestPanel)}
            className="btn-secondary flex items-center gap-2"
          >
            <FlaskConical className="w-4 h-4" />
            Test
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-glow flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error p-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Test Panel */}
      {showTestPanel && (
        <div className="card-glow p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300">Test Blocklist</h3>
          <textarea
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            placeholder="Paste text to test against the blocklist..."
            className="input-neural w-full h-24 resize-none"
          />
          <div className="flex items-center gap-3">
            <select
              value={testScope}
              onChange={(e) => setTestScope(e.target.value as 'text' | 'image')}
              className="input-neural"
            >
              <option value="text">Text scope</option>
              <option value="image">Image scope</option>
            </select>
            <button
              onClick={handleTest}
              disabled={testing || !testText.trim()}
              className="btn-glow flex items-center gap-2"
            >
              {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Run Test
            </button>
          </div>
          {testError && (
            <p className="mt-2 text-red-400 text-sm">{testError}</p>
          )}
          {testResults !== null && (
            <div className="mt-2">
              {testResults.length === 0 ? (
                <p className="text-green-400 text-sm">No violations found — text is clean.</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-red-400 text-sm font-semibold">
                    {testResults.length} violation{testResults.length !== 1 ? 's' : ''} found:
                  </p>
                  {testResults.map((v, i) => (
                    <div key={i} className="text-sm text-gray-300 pl-2 border-l-2 border-red-500/50">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${CATEGORY_COLORS[v.category] || CATEGORY_COLORS.other}`}>
                        {v.category}
                      </span>{' '}
                      <span className="code-text">{v.value}</span>{' '}
                      <span className="text-gray-500">({v.type})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3">
        <select
          value={filterScope}
          onChange={(e) => setFilterScope(e.target.value)}
          className="input-neural"
        >
          {SCOPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="input-neural"
        >
          {CATEGORY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div className="flex-1" />
        <span className="text-sm text-gray-500 self-center">
          {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-violet-400" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No blocklist entries yet. Click "Add Entry" to create one.
        </div>
      ) : (
        <div className="card-glow overflow-hidden">
          <table className="table-neural min-w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-3">Value</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Scope</th>
                <th className="text-left px-4 py-3">Category</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <span className="code-text text-sm">{entry.value}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">{entry.type}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${SCOPE_COLORS[entry.scope] || ''}`}>
                      {entry.scope}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.other}`}>
                      {entry.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(entry.id)}
                      className="text-red-400 hover:text-red-300 p-1"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4" onClick={() => { setShowAddModal(false); setAddError(null); }}>
          <div className="modal-content max-w-md w-full p-6 rounded-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold text-gradient mb-4">Add Blocklist Entry</h2>

            {addError && (
              <div className="alert-error p-2 rounded mb-3 text-sm">{addError}</div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Value</label>
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="Term, phrase, or regex pattern..."
                  className="input-neural w-full"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Match Type</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as 'word' | 'phrase' | 'pattern')}
                  className="input-neural w-full"
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Scope</label>
                <select
                  value={newScope}
                  onChange={(e) => setNewScope(e.target.value as 'text' | 'image' | 'both')}
                  className="input-neural w-full"
                >
                  <option value="both">Both (text + image)</option>
                  <option value="text">Text only</option>
                  <option value="image">Image only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Category</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as typeof newCategory)}
                  className="input-neural w-full"
                >
                  {CATEGORY_OPTIONS.filter((o) => o.value !== '').map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setShowAddModal(false); setAddError(null); }} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={adding || !newValue.trim()}
                className="btn-glow"
              >
                {adding ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
